/*
 * eslint-disable -- o AppLink ja encapsula WLink e WLinkButton, e o trigger de
 * submenu precisa ser um button nativo para carregar aria-haspopup e
 * aria-expanded.
 */
/* eslint-disable @alice-health/w-link, @alice-health/w-button */

import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Link } from '@/global/components/Link'
import { useClickOutside, useScrollDirection } from '@/global/hooks'
import { zIndex } from '@/global/constants/z-index'
import { mainMenu } from './Navbar.constants'
import { NavbarMenuPanel } from './NavbarMenuPanel'
import {
  getMenuPanelId,
  sendButtonClickedAnalyticEvent
} from './Navbar.helpers'

/*
 * O pill de hover fica no wrapper do link, fora do componente do design
 * system. A tipografia vem do proprio WLink, via variant e size.
 */
const itemClassName =
  'flex items-center justify-center rounded-gl-border-radius-md px-gl-spacing-04 py-[14px] transition-colors'

const NavbarMenu = () => {
  const [openTitle, setOpenTitle] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggersRef = useRef<Record<string, HTMLDivElement | null>>({})

  const close = useCallback(() => setOpenTitle(null), [])

  useClickOutside({ ref: containerRef, callback: close })

  /*
   * O Escape escuta no `document`, e nao no container: o painel abre tambem por
   * `mouseenter`, e nesse caso nada dentro do menu tem foco, entao um handler de
   * React nunca receberia a tecla.
   */
  useEffect(() => {
    if (!openTitle) return undefined

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return

      /*
       * Se o foco esta dentro do menu, ele volta para o trigger do painel que
       * fecha. Sem isso o foco cairia no `body`, porque o painel fechado sai da
       * ordem de tabulacao.
       */
      if (containerRef.current?.contains(document.activeElement)) {
        triggersRef.current[openTitle]
          ?.querySelector<HTMLElement>('a, button')
          ?.focus()
      }

      close()
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [openTitle, close])

  /*
   * Os itens somem na descida e voltam na subida, como o
   * `.n-navbar_wrap__center___links` de producao, que ganha `opacity: 0` e
   * `pointer-events: none` ao rolar para baixo.
   */
  const isHidden = useScrollDirection() === 'down'

  /* Um painel aberto nao pode ficar visivel depois que o trigger sai de cena. */
  useEffect(() => {
    if (isHidden) close()
  }, [isHidden, close])

  return (
    <div
      ref={containerRef}
      data-testid='navbar-menu'
      onMouseLeave={close}
      /*
       * `mouseleave` so dispara para quem o ponteiro entrou de fato, e o
       * ponteiro nunca entra no painel ao passar pelo trigger: os dois sao
       * irmaos, nao aninhados. Por isso o handler fica aqui, e a altura do
       * container cresce para a do painel aberto — assim o ponteiro continua
       * dentro dele no caminho do trigger ate o painel.
       */
      className={classNames(
        'navbar-theme navbar-panel-geometry',
        'absolute left-0 right-0 top-0 mx-auto hidden w-[588px] flex-col lg:flex',
        openTitle
          ? 'h-[calc(var(--navbar-panel-height)+var(--navbar-panel-top))]'
          : 'h-full'
      )}
    >
      <nav
        aria-label='Navegação principal'
        /*
         * Escondido na rolagem para baixo, o menu sai tambem da arvore de
         * acessibilidade e da ordem de tabulacao — o `tabIndex` de cada trigger
         * cuida da segunda parte. Sem isso o foco entraria num item de opacidade
         * zero, sem indicacao visual de onde ele esta.
         */
        aria-hidden={isHidden}
        /*
         * `justify-between` com 8px de padding, nao `justify-center`. E assim
         * que o header do Webflow distribui os itens dentro dos 588px: a folga
         * sobra entre os pills, o que da 16px de respiro visual. O `gap` de 4px
         * fica como minimo, para larguras em que a folga desaparece.
         */
        className={classNames(
          'relative z-[2] flex h-[var(--app-header-height)] items-center',
          'justify-between gap-gl-spacing-01 px-gl-spacing-02',
          /* 480ms e o `transition: opacity .48s ease` de producao. */
          'transition-opacity duration-[480ms] ease-[ease]',
          isHidden ? 'pointer-events-none opacity-0' : 'opacity-100'
        )}
      >
        {mainMenu.map(item => {
          const isOpen = openTitle === item.title
          const focus = () => setOpenTitle(item.submenu ? item.title : null)
          const panelId = item.submenu ? getMenuPanelId(item.title) : undefined

          const className = classNames(
            itemClassName,
            isOpen
              ? 'bg-gl-color-shades-white'
              : 'hover:bg-gl-color-transparent-black-05'
          )

          /*
           * Os handlers ficam no wrapper porque o AppLink nao repassa eventos
           * de mouse. `onFocus` borbulha no sistema de eventos do React, entao
           * o foco no link tambem abre o painel.
           */
          /*
           * O painel vem logo depois do proprio trigger, e nao no fim do menu:
           * assim o Tab a partir de um trigger aberto entra nos links do painel
           * em vez de saltar para o trigger seguinte. Ele e posicionado em
           * `absolute`, entao a ordem no DOM nao mexe no layout do `nav`.
           */
          return (
            <Fragment key={item.title}>
              <div
                ref={node => {
                  triggersRef.current[item.title] = node
                }}
                /*
                 * `z-[2]` mantem os itens na frente do painel, que agora e irmao
                 * deles dentro do `nav`. Sem isso o vidro do painel aberto, que
                 * comeca dentro da faixa do header, cobriria os proprios itens.
                 */
                className='navbar-item-type relative z-[2]'
                onMouseEnter={focus}
                onFocus={focus}
              >
                {item.href ? (
                  <Link
                    as='link'
                    quiet
                    variant='primary'
                    size='small'
                    href={item.href}
                    className={className}
                    /*
                     * O item com `href` e submenu tambem e um disclosure: sem
                     * estes atributos o leitor de tela anuncia apenas um link e
                     * o painel fica invisivel para ele.
                     */
                    ariaHasPopup={item.submenu ? true : undefined}
                    ariaExpanded={item.submenu ? isOpen : undefined}
                    ariaControls={panelId}
                    tabIndex={isHidden ? -1 : undefined}
                    onClick={() => sendButtonClickedAnalyticEvent(item.title)}
                  >
                    {item.title}
                  </Link>
                ) : (
                  <button
                    type='button'
                    aria-haspopup='true'
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    tabIndex={isHidden ? -1 : undefined}
                    /*
                     * O clique so abre. Um toggle fecharia o painel na mesma
                     * interacao que o abriu: qualquer clique de ponteiro emite
                     * mouseover antes, e o mouseover ja abriu. Para fechar
                     * existem o Escape, a saida do ponteiro e o clique fora.
                     */
                    onClick={() => {
                      sendButtonClickedAnalyticEvent(item.title)
                      setOpenTitle(item.title)
                    }}
                    /*
                     * O trigger nao e componente do design system, entao repete
                     * a tipografia do WLink size small e le a mesma variavel de
                     * cor.
                     */
                    className={classNames(
                      className,
                      'text-[color:var(--sys-color-content-active)] text-gl-font-size-md',
                      'leading-gl-line-height-rule-04'
                    )}
                  >
                    {item.title}
                  </button>
                )}
              </div>

              {item.submenu && panelId ? (
                <NavbarMenuPanel
                  id={panelId}
                  title={item.title}
                  submenu={item.submenu}
                  isOpen={isOpen}
                />
              ) : null}
            </Fragment>
          )
        })}
      </nav>

      <div
        aria-hidden
        data-testid='navbar-menu-backdrop'
        /*
         * `pointer-events-none` sempre: o backdrop e decorativo e e descendente
         * do container, entao capturar o clique faria o `useClickOutside` ler
         * como clique de dentro e o menu nao fecharia.
         *
         * Comeca abaixo do header. Um descendente com z-index negativo pinta na
         * frente do background do proprio header, entao cobrir `inset-0`
         * escureceria a barra junto com a pagina.
         */
        className={classNames(
          'pointer-events-none fixed bottom-0 left-0 right-0',
          'top-[var(--app-header-height)]',
          'bg-gl-color-transparent-black-30 transition-opacity duration-200',
          zIndex.global.navbarBackdrop,
          openTitle ? 'opacity-100' : 'opacity-0'
        )}
      />
    </div>
  )
}

export { NavbarMenu }
